import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Modal } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { format } from "date-fns";
import useNewExchangeAPI from "../../api/exchangeAPI";
import useCashOutApi from "../../../api/CashOutAPI";
import {
  setActionPayload,
  setActionType,
} from "../../../features/authorizationSlice";
import {
  setFlag,
  setIsPopup,
  setResponseDescription,
  setResponseInfo,
  setResponseTitle,
} from "../../../features/responseSlice";
import {
  setIsOpenCashAdvancePopup,
  setIsOpenSuccessModal,
} from "../../../features/AdvancePayrollConfirmPopupSlice";
import { restBillsData } from "../../../features/billsPageSlice";
import { setFeatureData } from "../../store/slices/appSlice";
import {
  capitalizeFirstLetters,
  formatCurrency,
} from "../../../features/ValueFormater";
import { ACTION_TYPES } from "../../../app/actionTypes";
import { roleAuth } from "../../../utils/functionAuthorization";
import { validationFlags } from "../../../utils/Constants";
import PaymentConfirmationPrePaid from "../../../components/PaymentComfirmationPrePaid";
import CashAdvancePopup from "../../../components/Payrolls/CashAdvancePopup";
import QRCodeDisplay from "../../../components/QrCodeDisplay";
import QrDownload from "../../../components/QrDownload";
import MapView from "../../../components/BobMap/MapView";
import "../../../New/Styles/components/modal.scss";

const FLAG_ICON_MAP = {
  0: "/Images/check.svg",
  1: "/Images/check.svg",
  435: "/Images/caution.svg",
  436: "/Images/caution.svg",
  437: "/Images/caution.svg",
};

function isSuccessPopup(flag, info, title, description) {
  const normalizedFlag = Number(flag);
  const normalizedInfoFlag = Number(info?.flagCode);
  const normalizedPopupFlag = Number(info?.Popup?.flagCode);
  const normalizedGlobal = Number(info?.GlobalCode);
  const successText = `${title || ""} ${description || ""}`.toLowerCase();
  const hasSuccessText = [
    "success",
    "successfully",
    "request sent",
    "completed",
    "approved",
    "cancelled",
  ].some((token) => successText.includes(token));

  return (
    [0, 1].includes(normalizedFlag) ||
    [0, 1].includes(normalizedInfoFlag) ||
    [0, 1].includes(normalizedPopupFlag) ||
    [0, 1].includes(normalizedGlobal) ||
    info?.IsSuccess === true ||
    info?.status === true ||
    hasSuccessText
  );
}

function localizeTaxPaymentText(value, moduleLabel, isTaxPayment) {
  if (!isTaxPayment || !moduleLabel || moduleLabel === "VAT") {
    return value;
  }

  return typeof value === "string"
    ? value.replace(/\bVAT\b/gi, moduleLabel)
    : value;
}

function PopupHeader({ icon, info, title }) {
  const imageSrc =
    Number(info?.GlobalCode) === 6
      ? "/Images/cashDelivery/cashpickup.png"
      : Number(info?.GlobalCode) === 5
        ? "/Images/cashDelivery/bobSucces.png"
        : icon;

  return (
    <div className="validation-popup__header">
      <div className="validation-popup__icon-shell">
        <img src={imageSrc} alt="Validation status" />
      </div>
      {info?.GlobalCode !== 7 ? (
        <h3
          className={`validation-popup__title ${
            info?.GlobalCode === 6 ? "is-cashin" : ""
          }`}
        >
          {title}
        </h3>
      ) : null}
    </div>
  );
}

function ResponseMessage({ clicked, description, info, nameEsim, priceEsim }) {
  if (info?.GlobalCode === 6) return null;

  return (
    <div className="validation-popup__message">
      <div
        className={info?.GlobalCode === 6 ? "is-cashin" : ""}
        dangerouslySetInnerHTML={{
          __html: clicked
            ? `You have successfuly topped up the $ ${priceEsim} ${nameEsim} esim`
            : description,
        }}
      />
    </div>
  );
}

function DefaultActions({
  clicked,
  flag,
  info,
  onClose,
  onExchange,
  navigate,
  suyool,
}) {
  const openSuyool = () => {
    window.location.href = "https://www.suyool.com";
  };

  if (flag === 11) {
    return (
      <div className="validation-popup__button-row">
        <button
          type="button"
          className="validation-popup__button validation-popup__button--secondary"
          onClick={onClose}
        >
          Not Now
        </button>
        <button
          type="button"
          className="validation-popup__button validation-popup__button--primary"
          onClick={onExchange}
        >
          Proceed
        </button>
      </div>
    );
  }

  if (flag === 84 || flag === 90) {
    return (
      <div className="validation-popup__stacked-actions">
        {flag === 84 ? (
          <button
            type="button"
            className="validation-popup__button validation-popup__button--primary"
            onClick={() => {
              onClose();
              navigate("/exchange");
            }}
          >
            Exchange
          </button>
        ) : null}
        {flag === 90 ? (
          <button
            type="button"
            className="validation-popup__button validation-popup__button--primary"
            onClick={() => {
              onClose();
              navigate("/cashin");
            }}
          >
            Add Money
          </button>
        ) : null}
        <button
          type="button"
          className="validation-popup__button validation-popup__button--secondary"
          onClick={onClose}
        >
          Cancel
        </button>
      </div>
    );
  }

  if (info?.InstallEsim) {
    return (
      <div className="validation-popup__single-action">
        <button
          type="button"
          className="validation-popup__button validation-popup__button--primary"
          onClick={() => {
            if (clicked) {
              navigate("/bills");
            } else {
              navigate("/bills/esim?comp=install-esim");
            }
            onClose();
          }}
        >
          {clicked ? "OK!" : "Install eSIM"}
        </button>
      </div>
    );
  }

  return (
    <div className="validation-popup__single-action">
      <button
        type="button"
        className="validation-popup__button validation-popup__button--primary"
        onClick={() => {
          if (suyool) {
            openSuyool();
            return;
          }
          onClose();
        }}
      >
        OK
      </button>
    </div>
  );
}

function BobCashoutContent({ copyText, info, onCopyCode, onSendSms, onShowMap }) {
  const isCopied = copyText === "Code Copied";
  const hasSmsAction = Boolean(info?.SendSms);

  return (
    <div className="validation-popup__cashout-card">
      <div className="validation-popup__info-grid">
        <div className="validation-popup__info-row">
          <span>Amount</span>
          <strong>{formatCurrency(info?.Amount)}</strong>
        </div>
        <div className="validation-popup__info-row">
          <span>Depositor Name</span>
          <strong>{capitalizeFirstLetters(info?.ReceiverName)}</strong>
        </div>
      </div>

      <div className="validation-popup__code-box">
        <p>Use this code at any BOB outlet to cash out:</p>
        <div>{info?.Code}</div>
      </div>

      <div
        className={`validation-popup__button-row ${
          hasSmsAction ? "" : "is-single"
        }`}
      >
        {hasSmsAction ? (
          <button
            type="button"
            className="validation-popup__button validation-popup__button--primary"
            onClick={onSendSms}
          >
            Send Code Via SMS
          </button>
        ) : null}
        <button
          type="button"
          className={`validation-popup__button validation-popup__button--secondary ${
            isCopied ? "is-copied" : ""
          }`}
          onClick={onCopyCode}
        >
          {copyText}
        </button>
      </div>

      <div className="validation-popup__warning-box">
        <div className="validation-popup__warning-icon">!</div>
        <p>
          Cash in amount only available at these {" "}
          <button
            type="button"
            className="validation-popup__inline-button"
            onClick={onShowMap}
          >
            BOB Branches
          </button>
        </p>
      </div>

      <div className="validation-popup__note">This code is only available for 24h</div>
    </div>
  );
}

function CashInQrContent({ info, onDownload }) {
  const canDownloadQr =
    (info?.Method === "cashout" &&
      roleAuth("cashout", "DOWNLOAD_CONFIRMATION_QR_CASH_OUT")) ||
    (info?.Method === "cashin" &&
      roleAuth("cashin", "DOWNLOAD_CONFIRMATION_QR_CASH_IN"));

  return (
    <div className="validation-popup__cashout-card">
      {info?.CashoutType === 2 ? (
        <div className="validation-popup__message">
          <div className="is-cashin">
            Kindly show the below QR to the Suyool courier to confirm the funds
            pick up.
          </div>
        </div>
      ) : null}

      {canDownloadQr ? (
        <>
          <div className="validation-popup__info-grid">
            <div className="validation-popup__info-row">
              <span>Amount Pick Up & Deposit</span>
              <strong>{formatCurrency(info?.Amount)}</strong>
            </div>
            <div className="validation-popup__info-row">
              <span>Address</span>
              <strong>{info?.Address}</strong>
            </div>
          </div>

          <button
            type="button"
            className="validation-popup__button validation-popup__button--primary"
            onClick={onDownload}
          >
            Download QR
          </button>
        </>
      ) : null}
    </div>
  );
}

function ButlerContent({ info, onDownload, onShare }) {
  return (
    <div className="validation-popup__butler-card">
      <h3>
        {info?.deliveryService === 1
          ? "Cash delivery request sent"
          : "Cash collection request sent"}
      </h3>

      <div className="validation-popup__delivery-card">
        <img
          src="/Images/cashDelivery/delivery-service.png"
          alt="Delivery service"
        />
        <div>
          <strong>{info?.Title}</strong>
          <p>{info?.PaymentTo === 1 ? "Business" : "Individual"}</p>
        </div>
      </div>

      <div className="validation-popup__info-grid">
        <div className="validation-popup__info-row">
          <span>
            {info?.deliveryService === 1
              ? "Total Amount the Recipient Will Receive"
              : "Total Amount to Collect"}
          </span>
          <strong>{formatCurrency(info?.Amount)}</strong>
        </div>
        <div className="validation-popup__info-row">
          <span>Additional Fees</span>
          <strong>{info?.Fees}</strong>
        </div>
        <div className="validation-popup__info-row">
          <span>Payment Reference</span>
          <strong>{info?.PaymentReference}</strong>
        </div>
        {info?.FileName && info?.FileUrl ? (
          <div className="validation-popup__info-row">
            <span>File</span>
            <a href={info.FileUrl} target="_blank" rel="noreferrer">
              {info.FileName}
            </a>
          </div>
        ) : null}
        <div className="validation-popup__info-row">
          <span>Person in Charge*</span>
          <strong>{info?.PersonInCharge}</strong>
        </div>
        {info?.MobileNo ? (
          <div className="validation-popup__info-row">
            <span>Phone Number</span>
            <strong>{info?.MobileNo}</strong>
          </div>
        ) : null}
        {info?.Email ? (
          <div className="validation-popup__info-row">
            <span>Email</span>
            <strong>{info?.Email}</strong>
          </div>
        ) : null}
        <div className="validation-popup__info-row">
          <span>Date & Time</span>
          <strong>
            {info?.DateTime
              ? `${format(new Date(info.DateTime), "dd MMM yyyy").toUpperCase()} - ${format(new Date(info.DateTime), "h:mm a").toLowerCase()}`
              : ""}
          </strong>
        </div>
        <div className="validation-popup__info-row">
          <span>Delivery Location</span>
          <strong>{info?.Address}</strong>
        </div>
      </div>

      {info?.QRCode ? (
        <div className="validation-popup__qr-section">
          <QRCodeDisplay link={info?.QRCode} />
        </div>
      ) : null}

      <div className="validation-popup__button-row">
        <button
          type="button"
          className="validation-popup__button validation-popup__button--secondary"
          onClick={onDownload}
        >
          Download QR
        </button>
        <button
          type="button"
          className="validation-popup__button validation-popup__button--primary"
          onClick={onShare}
        >
          Share QR
        </button>
      </div>
    </div>
  );
}

export default function ValidationPopup({
  descriptionOverride,
  onCloseOverride,
  onOpenQrAfterGenerate,
  openOverride,
  titleOverride,
}) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const [copyText, setCopyText] = useState("Copy Code");
  const [qr, setQr] = useState(null);
  const [showMap, setShowMap] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [isExchangeLoading, setIsExchangeLoading] = useState(false);
  const hasAutoRedirectedRef = useRef(false);

  const { flag, responseTitle, responseDescription, isPopup, info } =
    useSelector((state) => state.transferResponse);
  const { isOpenSuccessModal } = useSelector(
    (state) => state.advancePayrollConfirm,
  );
  const currentPage = useSelector((state) => state.billsPage.currentPage);
  const clicked = useSelector((state) => state.billsPage.esim.click);
  const priceEsim = useSelector((state) => state.billsPage.esim.Price);
  const nameEsim = useSelector((state) => state.billsPage.esim.name);
  const suyool = useSelector((state) => state.legalEnrollment.suyoolredirect);
  const actionType = useSelector((state) => state.authorization?.actionType);
  const taxPaymentNavigation = useSelector(
    (state) => state.app?.ui?.taxPaymentNavigation || null,
  );

  const displayResponseTitle = useMemo(
    () =>
      localizeTaxPaymentText(
        responseTitle,
        taxPaymentNavigation?.moduleLabel,
        Boolean(info?.vatPayment),
      ),
    [info?.vatPayment, responseTitle, taxPaymentNavigation?.moduleLabel],
  );
  const displayResponseDescription = useMemo(
    () =>
      localizeTaxPaymentText(
        responseDescription,
        taxPaymentNavigation?.moduleLabel,
        Boolean(info?.vatPayment),
      ),
    [info?.vatPayment, responseDescription, taxPaymentNavigation?.moduleLabel],
  );

  const { getExchangeRate, exchangeRate } = useNewExchangeAPI();
  const { SendSmsCode } = useCashOutApi();
  const isControlled = typeof openOverride === "boolean";

  const icon = useMemo(() => {
    if (info?.forceWarningIcon) {
      return "/Images/caution.svg";
    }

    if (isSuccessPopup(flag, info, displayResponseTitle, displayResponseDescription)) {
      return "/Images/check.svg";
    }

    if (flag === null || flag === undefined) {
      return "/Images/caution.svg";
    }
    return FLAG_ICON_MAP[Number(flag)] || "/Images/caution.svg";
  }, [displayResponseDescription, displayResponseTitle, flag, info]);

  const isInlineExchangeFlow = location.pathname?.toLowerCase() === "/exchange";
  const normalizedPathname = String(location.pathname || "").toLowerCase();

  const resolvePrimaryNavigation = useCallback((pathname) => {
    if (!pathname || pathname === "/") {
      return { path: pathname, state: null };
    }

    if (pathname.startsWith("/bills/")) {
      return { path: "/bills", state: null };
    }

    if (pathname.startsWith("/payroll/")) {
      return { path: "/payroll", state: null };
    }

    if (pathname.startsWith("/pendingapprovals/")) {
      return { path: "/pendingapprovals", state: null };
    }

    if (pathname.startsWith("/cardtransactions/")) {
      return { path: "/cardtransactions", state: null };
    }

    if (pathname === "/suyooltosuyool") {
      return {
        path: pathname,
        state: { name: "Suyool To Suyool" },
      };
    }

    if (pathname === "/cashout") {
      return {
        path: pathname,
        state: { name: "Orders" },
      };
    }

    if (pathname === "/cashin") {
      return {
        path: pathname,
        state: { name: "Cash Deposit" },
      };
    }

    if (pathname === "/butlerservice") {
      return {
        path: pathname,
        state: { name: "Orders" },
      };
    }

    if (pathname === "/suyooltobsl") {
      return {
        path: pathname,
        state: { name: "Suyool To BSL" },
      };
    }

    if (pathname === "/bsltosuyool") {
      return {
        path: pathname,
        state: { name: "BSL To Suyool" },
      };
    }

    if (pathname === "/banktransfers") {
      return {
        path: pathname,
        state: null,
      };
    }

    if (pathname === "/bankaccounttransfers") {
      return {
        path: pathname,
        state: null,
      };
    }

    return { path: pathname, state: null };
  }, []);

  const openModal = useCallback(() => setIsOpen(true), []);
  const closeModal = useCallback(() => setIsOpen(false), []);

  useEffect(() => {
    if (isControlled) return;

    if (isInlineExchangeFlow) {
      closeModal();
      return;
    }

    if (isPopup && isOpenSuccessModal) {
      openModal();

      if (!hasAutoRedirectedRef.current) {
        hasAutoRedirectedRef.current = true;
        const navigationTarget = resolvePrimaryNavigation(normalizedPathname);
        const targetPath = navigationTarget?.path || normalizedPathname;
        const targetState = navigationTarget?.state || null;

        window.dispatchEvent(
          new CustomEvent("new:popup-auto-redirect", {
            detail: {
              actionType,
              currentPath: normalizedPathname,
              targetPath,
              targetState,
            },
          }),
        );

        if (
          targetPath &&
          (targetPath !== normalizedPathname || targetState)
        ) {
          navigate(targetPath, {
            replace: true,
            state: targetState || undefined,
          });
        }
      }
      return;
    }

    hasAutoRedirectedRef.current = false;
    closeModal();
  }, [
    actionType,
    closeModal,
    isControlled,
    isInlineExchangeFlow,
    isOpenSuccessModal,
    isPopup,
    navigate,
    normalizedPathname,
    openModal,
    resolvePrimaryNavigation,
  ]);

  useEffect(() => {
    if (isControlled) return;

    if (flag === 11) {
      getExchangeRate();
    } else if (flag === 22) {
      dispatch(setIsOpenCashAdvancePopup(true));
      closeModal();
      return;
    }

    dispatch(setIsOpenSuccessModal(true));
  }, [closeModal, dispatch, flag, getExchangeRate, isControlled]);

  const resetPopupState = useCallback(() => {
    const shouldRedirectToTaxPaymentOverview = Boolean(
      info?.redirectToVatOverview,
    );
    const taxPaymentStateKey = taxPaymentNavigation?.stateKey || "vat";
    const taxPaymentRoute = taxPaymentNavigation?.route || "/vat";

    setShowMap(false);
    setQr(null);
    setCopyText("Copy Code");
    setIsExchangeLoading(false);
    dispatch(setIsPopup(false));
    dispatch(setResponseTitle(null));
    dispatch(setResponseDescription(null));
    setTimeout(() => {
      dispatch(setFlag(null));
    }, 3000);

    if (currentPage !== "pin-confirm") {
      dispatch(restBillsData());
    }

    if (flag !== validationFlags.payrollError) {
      dispatch(setResponseInfo(null));
    }

    if (shouldRedirectToTaxPaymentOverview) {
      dispatch(
        setFeatureData({
          section: "ui",
          key: taxPaymentStateKey,
          value: {
            step: "overview",
            showValidationModal: false,
            extracting: false,
            extractionError: "",
          },
        }),
      );
      dispatch(
        setFeatureData({
          section: "forms",
          key: taxPaymentStateKey,
          value: {
            selectedFile: null,
            fileName: "",
          },
        }),
      );
      navigate(taxPaymentRoute);
    }

    dispatch(
      setFeatureData({
        section: "ui",
        key: "taxPaymentNavigation",
        value: null,
      }),
    );
    closeModal();
  }, [
    closeModal,
    currentPage,
    dispatch,
    flag,
    info?.redirectToVatOverview,
    navigate,
    taxPaymentNavigation?.route,
    taxPaymentNavigation?.stateKey,
  ]);

  const initiateExchange = useCallback(() => {
    setIsExchangeLoading(true);
    dispatch(setActionType(ACTION_TYPES.INITIATE_EXCHANGE));
    dispatch(
      setActionPayload({
        ExchangeFromAmount: info?.amount,
        ExchangeFromCurrencyID: info?.currencyFrom,
        ExchangeToCurrencyID: info?.currencyTo,
        ExchangeBuy: exchangeRate?.Buy,
        ExchangeSell: exchangeRate?.Sell,
      }),
    );
    resetPopupState();
    onOpenQrAfterGenerate?.();
  }, [dispatch, exchangeRate?.Buy, exchangeRate?.Sell, info, onOpenQrAfterGenerate, resetPopupState]);

  const copyCodeToClipboard = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(info?.Code || "");
      setCopyText("Code Copied");
      setTimeout(() => setCopyText("Copy Code"), 1400);
    } catch (error) {
      setCopyText("Copy Code");
    }
  }, [info?.Code]);

  const sendSmsCode = useCallback(async () => {
    await SendSmsCode(info?.phone, info?.Code);
    resetPopupState();
  }, [SendSmsCode, info?.Code, info?.phone, resetPopupState]);

  if (flag === 22) {
    return <CashAdvancePopup />;
  }

  const modalIsOpen = isControlled ? openOverride : isOpen;
  const handleModalClose = isControlled
    ? (onCloseOverride || (() => {}))
    : resetPopupState;

  return (
    <Modal
      centered
      show={modalIsOpen}
      onHide={handleModalClose}
      className="validation-popup-layer"
      backdropClassName="validation-popup-backdrop"
      dialogClassName="validation-popup-modal"
    >
      <Modal.Body className="validation-popup">
        {isControlled ? (
          <>
            <button
              type="button"
              className="validation-popup__close"
              onClick={handleModalClose}
              aria-label="Close popup"
            >
              &times;
            </button>
            <div className="validation-popup__header">
              <div className="validation-popup__icon-shell">
                <img src="/Images/caution.svg" alt="Validation status" />
              </div>
              <h3 className="validation-popup__title">{titleOverride}</h3>
            </div>
            <div className="validation-popup__message">
              <div>{descriptionOverride}</div>
            </div>
            <div className="validation-popup__single-action">
              <button
                type="button"
                className="validation-popup__button validation-popup__button--primary"
                onClick={handleModalClose}
              >
                OK
              </button>
            </div>
          </>
        ) : showMap ? (
          <div className="validation-popup__map">
            <MapView onBack={() => setShowMap(false)} />
          </div>
        ) : (
          <>
            <button
              type="button"
              className="validation-popup__close"
              onClick={resetPopupState}
              aria-label="Close popup"
            >
              &times;
            </button>

            {flag === 101 ? (
              <PaymentConfirmationPrePaid />
            ) : (
              <>
                <PopupHeader
                  icon={icon}
                  info={info}
                  title={displayResponseTitle}
                />

                <ResponseMessage
                  clicked={clicked}
                  description={displayResponseDescription}
                  info={info}
                  nameEsim={nameEsim}
                  priceEsim={priceEsim}
                />

                {info?.GlobalCode === 5 ? (
                  <BobCashoutContent
                    copyText={copyText}
                    info={info}
                    onCopyCode={copyCodeToClipboard}
                    onSendSms={sendSmsCode}
                    onShowMap={() => setShowMap(true)}
                  />
                ) : null}

                {info?.GlobalCode === 6 ? (
                  <CashInQrContent
                    info={info}
                    onDownload={() => setQr(info?.QRCode)}
                  />
                ) : null}

                {info?.GlobalCode === 7 ? (
                  <ButlerContent
                    info={info}
                    onDownload={() => setQr(info?.QRCode)}
                    onShare={() => navigator?.share?.({ url: info?.QRCode })}
                  />
                ) : null}

                {![5, 6, 7].includes(info?.GlobalCode) ? (
                  <DefaultActions
                    clicked={clicked}
                    flag={flag}
                    info={info}
                    navigate={navigate}
                    onClose={resetPopupState}
                    onExchange={initiateExchange}
                    suyool={suyool}
                  />
                ) : null}

                {isExchangeLoading ? (
                  <div className="validation-popup__loading">Preparing validation...</div>
                ) : null}
              </>
            )}
          </>
        )}

        <QrDownload qrCodeData={qr} setQrData={setQr} />
      </Modal.Body>
    </Modal>
  );
}
